import {
  LegacyOrder,
  LegacyOrderLine,
  PricingModel,
  SAME_DAY_STORAGE_DAY_PRODUCT_KEY,
  SAME_DAY_STORAGE_HOUR_PRODUCT_KEY,
  STORAGE_DAY_PRODUCT_KEY,
  STORAGE_HOUR_PRODUCT_KEY,
} from '@luggagehero/shared/interfaces';

import { SharedUtilProduct } from './shared-util-product';

export abstract class SharedUtilOrder {
  /**
   * The storage price **per bag for the whole booking**, summed over the order's storage lines.
   *
   * ⚠️ Not a per-day or per-hour rate, despite the name. The order conversion folds the period count into
   * each storage line's `unitPrice` (a 3-day daily line carries `dailyRate * 3`), so for a multi-day
   * booking this returns three days' storage, not the daily rate. Hourly *quotes* happen to come back as a
   * bare hourly rate only because the server sends `units: 0` there and no multiplier is applied — so the
   * two pricing models are not even on the same footing.
   *
   * For anything that labels a price with a time unit, use {@link perBagStoragePeriodRate}. Reading this
   * value as a rate is what shipped "£13.50/bag/day" for a £4.50/day shop (AM-4637).
   *
   * @param value The order for which to calculate the total storage price per bag
   * @returns The total storage price per bag, across the booking's whole period
   */
  public static perBagStorageRate(value: LegacyOrder): number {
    const storageRate = SharedUtilOrder.storageAndSavingsLines(value).reduce(
      (acc, orderLine) => acc + orderLine.unitPrice,
      0,
    );

    return storageRate;
  }

  /**
   * The storage lines plus the savings lines split out of them (AM-4961). The order summary shows a volume,
   * multi-day or advance booking discount on its own negative line and puts the storage line back at the
   * full rate, so a rate read from the storage line alone is the rate before the discount — not what the
   * traveler pays. Summing both gives the discounted rate.
   */
  private static storageAndSavingsLines(value: LegacyOrder): LegacyOrderLine[] {
    return value.orderLines.filter(
      (orderLine) => SharedUtilProduct.isStorageFee(orderLine.product) || !!orderLine.discountsStorageProductKey,
    );
  }

  /**
   * The storage rate **per bag per period** — per day for a daily order, per hour for an hourly one
   * (AM-4637).
   *
   * This is the number a card may legitimately label `/day` or `/hour`. It divides each storage line's
   * `unitPrice` back down by the `storagePeriods` the conversion folded into it, so a £4.50/day shop reads
   * £4.50 whether the traveler picked one day or five — where {@link perBagStorageRate} would say £22.50.
   *
   * Lines with no `storagePeriods` (or 0) are treated as a single period, which is the correct reading for
   * an hourly quote and a safe one for any line the conversion did not multiply.
   */
  public static perBagStoragePeriodRate(value: LegacyOrder): number {
    return SharedUtilOrder.perPeriodSum(SharedUtilOrder.storageAndSavingsLines(value));
  }

  /**
   * What booking ahead saves per bag per period on this quote (AM-4961, option 4d): the shop's same-day
   * rate minus the rate the quote charges, to the cent. The advance price is set directly on the
   * hourly/daily products (no discount modifier), so the saving only shows against the same-day product.
   * 0 when the quote already charges the same-day rate (a drop-off today) or the shop has no same-day rate.
   *
   * Compared on the storage line's own rate, before the volume, multi-day or honeymoon savings lines: those
   * apply whenever the traveler books, so netting them in would credit booking ahead with them — a drop-off
   * today with a multi-bag discount would read as a book-ahead saving.
   */
  public static bookAheadSavingPerBag(value: LegacyOrder, sameDayRate: number | null | undefined): number {
    const rate = SharedUtilOrder.perBagStoragePeriodRateBeforeSavings(value);
    if (!sameDayRate || !(sameDayRate > rate)) {
      return 0;
    }
    return Math.round((sameDayRate - rate) * 100) / 100;
  }

  /**
   * The order with each day-ahead storage line renamed as such (AM-4961): "Storage, pay as you go" becomes
   * "Day-ahead storage, pay as you go", "Storage, 2 days" becomes "Day-ahead storage, 2 days". Lines are
   * copied, never mutated.
   *
   * A line is day-ahead when it is the `hourlyRate` / `dailyRate` product and the shop's same-day price for
   * that model is higher than the line's own per-period rate. At a shop without same-day prices those
   * products are simply the price, on the day too, so they keep their usual name.
   *
   * @param sameDayRates The shop's products, or anything with `{ key, cost }`, to read the same-day rates from
   * @param names The translated names: the day-ahead pay-as-you-go name, and the "Storage" label period
   *   lines start with and its day-ahead version
   */
  public static withDayAheadStorageNames(
    value: LegacyOrder,
    sameDayRates: { key: string; cost?: number }[] | null | undefined,
    names: { dayAheadPayAsYouGo: string; storage: string; dayAheadStorage: string },
  ): LegacyOrder {
    const rate = (key: string) => sameDayRates?.find((product) => product.key === key)?.cost;
    const sameDayFor: Record<string, number | undefined> = {
      [STORAGE_HOUR_PRODUCT_KEY]: rate(SAME_DAY_STORAGE_HOUR_PRODUCT_KEY),
      [STORAGE_DAY_PRODUCT_KEY]: rate(SAME_DAY_STORAGE_DAY_PRODUCT_KEY),
    };
    let changed = false;
    const orderLines = value.orderLines.map((line) => {
      const productKey = line.product?._id;
      const sameDay = productKey ? sameDayFor[productKey] : undefined;
      const periods = line.storagePeriods && line.storagePeriods > 0 ? line.storagePeriods : 1;
      if (!sameDay || !(sameDay > line.unitPrice / periods)) {
        return line;
      }
      // Which line it is comes from the product key (checked above) and `isPricing` — the pay-as-you-go
      // quote line — never from the translated name. The name is only rewritten: a period line keeps
      // its ", 2 days" ending, and a name that does not start with the storage label is left alone.
      const name = line.product.name ?? '';
      const renamed = line.isPricing
        ? names.dayAheadPayAsYouGo
        : name.startsWith(`${names.storage},`)
          ? `${names.dayAheadStorage}${name.slice(names.storage.length)}`
          : name;
      if (renamed === name) {
        return line;
      }
      changed = true;
      return { ...line, product: { ...line.product, name: renamed } };
    });
    return changed ? { ...value, orderLines } : value;
  }

  /**
   * The whole-number percentage a storage discount takes off the storage line it discounts, e.g. 10 for
   * the advance booking discount (AM-4961) — or 0 when the order carries no savings line for that modifier.
   */
  public static storageSavingsPercentage(value: LegacyOrder, modifierKey: string): number {
    for (const saving of value.orderLines) {
      if (!saving.discountsStorageProductKey || saving.product.name !== modifierKey) {
        continue;
      }
      const storage = value.orderLines.find(
        (line) =>
          SharedUtilProduct.isStorageFee(line.product) && line.product._id === saving.discountsStorageProductKey,
      );
      if (storage && storage.unitPrice > 0) {
        return Math.round((-saving.unitPrice / storage.unitPrice) * 100);
      }
    }
    return 0;
  }

  /**
   * {@link perBagStoragePeriodRate} **before** any storage discount shown on its own line — volume,
   * multi-day or advance booking (AM-4961). Equal to it when the order carries no such discount; a card
   * can show this struck through next to the discounted rate.
   */
  public static perBagStoragePeriodRateBeforeSavings(value: LegacyOrder): number {
    return SharedUtilOrder.perPeriodSum(
      value.orderLines.filter((orderLine) => SharedUtilProduct.isStorageFee(orderLine.product)),
    );
  }

  private static perPeriodSum(orderLines: LegacyOrderLine[]): number {
    return orderLines.reduce((acc, orderLine) => {
      const periods = orderLine.storagePeriods && orderLine.storagePeriods > 0 ? orderLine.storagePeriods : 1;
      return acc + orderLine.unitPrice / periods;
    }, 0);
  }

  /**
   * The order's storage `unitPrice`, as the hourly or the daily rate depending on its storage line's product
   * (AM-4961). An order carries exactly one storage line — advance, walk-in, guest or same-day — so it is
   * found by the storage key sets rather than by a list of keys that falls behind when a product is added.
   *
   * A 0 price reads as no rate (`undefined`), which is what the legacy `a || b || c` lookups this replaces
   * returned for free guest storage.
   */
  public static storageUnitPrices(value: LegacyOrder): { hourlyRate?: number; dailyRate?: number } {
    const orderLines = value?.orderLines ?? [];
    const hourly = orderLines.find((orderLine) => SharedUtilProduct.isHourlyStorageFee(orderLine.product));
    const daily = orderLines.find((orderLine) => SharedUtilProduct.isDailyStorageFee(orderLine.product));
    // Net any savings line split out of the storage line, so the rate is the one the traveler pays.
    const netted = (line?: LegacyOrderLine) =>
      line
        ? orderLines
            .filter((orderLine) => orderLine.discountsStorageProductKey === line.product._id)
            .reduce((acc, orderLine) => acc + orderLine.unitPrice, line.unitPrice)
        : undefined;

    return { hourlyRate: netted(hourly) || undefined, dailyRate: netted(daily) || undefined };
  }

  /**
   * Calcuates the service fee (price per bag) by accumulating the unit price for all service order lines in the
   * provided order
   * @param value The order for which to calculate the total service fee per bag
   * @returns The total service fee per bag for the given order
   */
  public static perBagServiceFee(value: LegacyOrder): number {
    const serviceOrderLines = value.orderLines.filter((orderLine) => SharedUtilProduct.isServiceFee(orderLine.product));
    const serviceFee = serviceOrderLines.reduce((acc, orderLine) => acc + orderLine.unitPrice, 0);

    return serviceFee;
  }

  /**
   * The order as a summary should present it: **un-opted optional lines dropped** (AM-4653).
   *
   * A quote carries every optional add-on it could price, each flagged `optional` with a `selected`
   * verdict — so an order the traveler has declined insurance on still contains an insurance line. In
   * a read-only summary, where the add-on was chosen on its own step and cannot be toggled here, a
   * struck-through "not selected" row is noise. The total is unaffected either way: a deselected
   * optional line contributes nothing.
   *
   * Returns a shallow copy — the quote itself is shared state and must not be filtered in place.
   *
   * `?? []` guards a quote that arrives without lines: callers feed this straight into the chrome
   * around a pay CTA, so throwing here would take the CTA down over a cosmetic summary.
   */
  public static withSelectedAddOns(value: LegacyOrder): LegacyOrder {
    const orderLines = (value.orderLines ?? []).filter((line) => !(line.optional && line.selected === false));
    return { ...value, orderLines };
  }

  /**
   * The storage-rate label on a pricing-model card, e.g. `$4.50/bag/day` (AM-4637).
   *
   * The **time unit is always present**: a bare `$4.50/bag` never says what period the price buys,
   * and it leaves the hourly and daily cards looking like two prices for the same thing.
   *
   * `accumulated` switches the figure from the per-bag rate to the price for every bag in the
   * booking. The `/bag` segment then drops out, because the amount is no longer per bag — 2 bags at
   * `$4.50/bag/day` reads `$9/day`.
   *
   * Both the booking wizard's select-storage step and the guest drop-off flow's storage-selection
   * step render this card, and they previously composed the label separately — so this is the one
   * home for it, and the two cannot drift apart again.
   *
   * Takes already-formatted, already-translated fragments so it stays pure string composition:
   * currency formatting belongs to `SharedPricingService` and the words to the translate service.
   */
  public static storageRateLabel(parts: {
    perBagRate: string;
    accumulatedRate: string;
    bagLabel: string;
    unitLabel: string;
    accumulated: boolean;
  }): string {
    const { perBagRate, accumulatedRate, bagLabel, unitLabel, accumulated } = parts;

    return accumulated ? `${accumulatedRate}/${unitLabel}` : `${perBagRate}/${bagLabel}/${unitLabel}`;
  }

  /**
   * The pricing models this booking can be switched between, and the per-bag rate of each, read from a
   * quote of the booking under each model (AM-5115). A model is sold when its quote carries a storage line
   * of that model priced above 0: the API drops an unpriced storage line from a quote without an error, so
   * offering such a model would quote a stay with no storage at all. Hourly is also not sold where the
   * business switched it off for the location (`hourlyRateDisabled`, the app setting the booking flows
   * honour). The rate is the storage line's own per-bag, per-period rate, so it is what the switch would
   * charge, same-day product and savings included, never a list price.
   */
  public static sellablePricingModels(
    quotes: Partial<Record<PricingModel, LegacyOrder | undefined>>,
    hourlyRateDisabled: boolean,
  ): { models: PricingModel[]; rates: Partial<Record<PricingModel, number>> } {
    const models: PricingModel[] = [];
    const rates: Partial<Record<PricingModel, number>> = {};
    for (const model of ['hourly', 'daily'] as PricingModel[]) {
      const quote = quotes[model];
      if (!quote || SharedUtilOrder.storagePricingModel(quote) !== model) {
        continue;
      }
      const rate = SharedUtilOrder.perBagStoragePeriodRate(quote);
      if (!(rate > 0) || (model === 'hourly' && hourlyRateDisabled)) {
        continue;
      }
      models.push(model);
      rates[model] = rate;
    }
    return { models, rates };
  }

  /**
   * The pricing model an order charges on, read from its storage line by family (AM-5115): the advance,
   * same-day, walk-in and guest products of one model are the same model. This is what the API charges by
   * at check-out, so it decides a booking's current model over its `price.pricingModel`, which can disagree.
   * Undefined when the order has no storage line.
   */
  public static storagePricingModel(value: LegacyOrder | undefined): PricingModel | undefined {
    const line = value?.orderLines?.find((orderLine) => SharedUtilProduct.isStorageFee(orderLine.product));
    if (!line) {
      return undefined;
    }
    return SharedUtilProduct.isHourlyStorageFee(line.product) ? 'hourly' : 'daily';
  }
}
